import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableCell, TableHead, TableRow, TableSortLabel, TableBody, CircularProgress, LinearProgress, Switch } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { userService } from '../../../shared/services/userService';
import { Snackbar, Alert, Box } from '@mui/material';
import { Visibility as VisibilityIcon, Sync as SyncIcon } from '@mui/icons-material';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { SyncProgressBanner, type SyncProgressDetails } from '../../../shared/components/SyncProgressBanner';

interface User {
    id: string;
    userId: number;
    name: string;
    email: string | null;
    mobile: string | null;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    state: string | null;
    pincode: string;
    status: string;
    isActive: boolean;
    role: string;
    usertype: string;
    type?: string;
}

export const Users: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [users, setUsers] = useState<User[]>([]);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [syncing, setSyncing] = useState(false);
    const [syncStats, setSyncStats] = useState<{ synced?: number; total?: number; remaining?: number; failed?: number } | null>(null);
    const [syncDetails, setSyncDetails] = useState<SyncProgressDetails | null>(null);

    const getUsersList = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await userService.list({
                page: 1,
                limit: 1000
            });
            const listData = response?.data?.items || response?.data || [];
            const mappedUsers = Array.isArray(listData) ? listData : [];
            setUsers(mappedUsers.map((u: any) => ({
                ...u,
                id: u._id || u.id || u.userId,
                isActive: u.isActive ?? u.is_active ?? (u.status === 'Active'),
                role: u.usertype || 'customer'
            })));
        } catch (error) {
            console.error('Error fetching users:', error);
            setSnackbar({ open: true, message: 'Error loading users' });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        getUsersList();
    }, [getUsersList]);

    const handleStatusChange = async (user: User) => {
        try {
            const newStatus = !user.isActive;
            await userService.updateStatus(user.id, { isActive: newStatus });
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, isActive: newStatus } : u));
            setSnackbar({ open: true, message: 'User status updated successfully' });
        } catch (error: any) {
            console.error('Error updating status:', error);
            setSnackbar({ open: true, message: error?.response?.data?.message || 'Failed to update status' });
        }
    };

    const handleRoleToggle = async (user: User) => {
        try {
            await userService.toggleRole(user.id);
            setUsers(prev => prev.map(u => u.id === user.id ? {
                ...u,
                role: u.role?.toLowerCase() === 'delivery' ? 'customer' : 'delivery',
                usertype: u.role?.toLowerCase() === 'delivery' ? 'customer' : 'delivery'
            } : u));
            setSnackbar({ open: true, message: 'User role updated successfully' });
        } catch (error: any) {
            console.error('Error toggling role:', error);
            setSnackbar({ open: true, message: error?.response?.data?.message || 'Failed to toggle role' });
        }
    };

    const filteredUsers = React.useMemo(() => {
        let currentUsers = [...users];
        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            currentUsers = currentUsers.filter(user =>
                (user.name?.toLowerCase() || '').includes(q) ||
                (user.email?.toLowerCase() || '').includes(q) ||
                (user.mobile?.toLowerCase() || '').includes(q) ||
                (user.city?.toLowerCase() || '').includes(q)
            );
        }

        if (sortData?.active) {
            currentUsers.sort((a: any, b: any) => {
                const valA = a[sortData.active] || '';
                const valB = b[sortData.active] || '';
                if (sortData.direction === 'asc') {
                    return valA > valB ? 1 : -1;
                } else {
                    return valA < valB ? 1 : -1;
                }
            });
        }
        return currentUsers;
    }, [users, searchInput, sortData]);

    const paginatedUsers = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredUsers.slice(start, start + pageSize);
    }, [filteredUsers, currentPage, pageSize]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handleSort = (property: string) => {
        const isAsc = sortData?.active === property && sortData?.direction === 'asc';
        setSortData({
            active: property,
            direction: isAsc ? 'desc' : 'asc'
        });
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handleViewUser = (user: User) => {
        navigate(`/users/view/${user.id}`);
    };

    const handleSync = async () => {
        let pollInterval: any;
        try {
            setSyncing(true);
            setSyncDetails(null);
            setSyncStats(null);

            // Start the sync process
            await userService.sync();

            // Function to check status and update UI
            const checkStatus = async () => {
                try {
                    const statusRes = await userService.getSyncStatus();
                    if (statusRes.success) {
                        setSyncDetails(statusRes.data as any);

                        if (statusRes.data.isRunning) {
                            await getUsersList();
                        } else {
                            if (pollInterval) clearInterval(pollInterval);
                            setSyncing(false);
                            if ((statusRes.data as any).lastError) {
                                setSnackbar({ open: true, message: (statusRes.data as any).lastError });
                            } else {
                                setSnackbar({ open: true, message: 'Users synced successfully' });
                            }
                            await getUsersList();
                        }
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                    if (pollInterval) clearInterval(pollInterval);
                    setSyncing(false);
                }
            };

            // Immediate check
            await checkStatus();

            // Start polling
            pollInterval = setInterval(checkStatus, 3000);

        } catch (error: any) {
            setSnackbar({ open: true, message: error?.response?.data?.message || 'Failed to start sync' });
            setSyncing(false);
        }
    };

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Users
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search users..."
                        className="w-64"
                    />
                    {(isSuperAdmin() || hasPermission('users.add')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<SyncIcon />}
                            onClick={handleSync}
                            disabled={syncing || isLoading}
                            loading={syncing}
                        >
                            {syncing ? 'Syncing...' : 'Sync Users'}
                        </AppButton>
                    )}
                </div>
            </div>

            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">
                {/* Sync Progress Banner */}
                <SyncProgressBanner syncing={syncing} details={syncDetails} />
                {!syncing && syncStats != null && (
                    <div className="mt-2 mb-6 p-4 rounded-lg bg-green-50 border border-green-200">
                        <div className="text-green-800 font-medium mb-2">Sync result</div>
                        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-1 text-green-700 text-sm">
                            <span><strong>Synced:</strong> {syncStats.synced}</span>
                            {syncStats.total != null && <span><strong>Total:</strong> {syncStats.total}</span>}
                        </div>
                    </div>
                )}

                <div className="flex flex-col w-full flex-1">
                    {isLoading && users.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && users.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">
                                        <TableSortLabel
                                            active={sortData?.active === 'name'}
                                            direction={sortData?.active === 'name' ? sortData.direction : 'asc'}
                                            onClick={() => handleSort('name')}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Mobile</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Email</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">City</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Role</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700" width={100}>Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredUsers.length === 0}
                            emptyColSpan={8}
                        >
                            {paginatedUsers.map((item, i) => (
                                <TableRow key={item.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <TableCell className="!text-gray-700">{(currentPage * pageSize) + i + 1}</TableCell>
                                    <TableCell className="!text-gray-900">{item.name}</TableCell>
                                    <TableCell className="!text-gray-700">{item.mobile || 'N/A'}</TableCell>
                                    <TableCell className="!text-gray-700">{item.email || 'N/A'}</TableCell>
                                    <TableCell className="!text-gray-700">{item.city || 'N/A'}</TableCell>
                                    <TableCell className="!text-gray-700">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${item.role?.toLowerCase() === 'delivery' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {item.role || 'customer'}
                                            </span>
                                            <Switch
                                                checked={item.role?.toLowerCase() === 'delivery'}
                                                onChange={() => handleRoleToggle(item)}
                                                color="secondary"
                                                size="small"
                                                disabled={isLoading || (!isSuperAdmin() && !hasPermission('users.update'))}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={item.isActive}
                                            onChange={() => handleStatusChange(item)}
                                            color="primary"
                                            disabled={isLoading || (!isSuperAdmin() && !hasPermission('users.update'))}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <AppButton
                                            variant="secondary"
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => handleViewUser(item)}
                                        >
                                            View
                                        </AppButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredUsers.length}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(0);
                    }}
                />
            </div>

            <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity="info">{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};
